/* eslint-disable */

import { useEffect, useState } from 'react'
import scanner from '../../../src/assets/PaymentGateway/document_scanner.svg'
import spark from '../../assets/PaymentGateway/spark.svg'
import account from '../../assets/PaymentGateway/account_balance.svg'
import additionalImage from '../../assets/PaymentGateway/account_balance_blue.svg'
import tick from '../../assets/PaymentGateway/Frame 354190.svg'
import { Button } from '../../Components/ui/button'
import { useLocation, useNavigate } from 'react-router-dom'
import { get, post } from '../../http/restservice'
import { Input } from '../../Components/ui/input'
import { ChevronLeft } from 'lucide-react'
import { useRecoilState, useRecoilValueLoadable } from 'recoil'
import { BankDetails } from '../../store/AccountDetailsStore'

import PaymentStatusModal from '../../Components/modals/OneTimePayment'
import env from '../../env/env'
import { AccountBalance } from '@mui/icons-material'
import MFUpiPayment from './MFUpiPayment'
import PostHogTracker from '../../utils/PostHogTracker'
import { userDetailsState } from '../LoginState'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'
import { useErrorPopup } from '../../hooks/useErrorPopup'

const BankAccountItem = ({ bank, isSelected, onSelect, tickIcon, bankLogosUrl }) => {
  const [iconSrc, setIconSrc] = useState(() => {
    const branch = bank.ifscCode?.match(/^[A-Za-z]{4}/)?.[0]?.toLowerCase()
    return branch ? `${bankLogosUrl}/${branch}/symbol.svg` : null
  })

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        onSelect(bank.accountNo)
      }}
      className={`w-[320px] p-4 flex items-center gap-4 rounded-[16px] cursor-pointer transition-all duration-200 ease-in-out border ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-transparent hover:bg-[#ecf0f4]'
        }`}
    >
      <div className='flex-shrink-0'>
        {iconSrc ? (
          <img
            src={iconSrc}
            alt='bank logo'
            onError={() => setIconSrc(null)}
            height={24}
            width={24}
          />
        ) : (
          <AccountBalance className='text-primary-200' />
        )}
      </div>
      <div className='flex flex-col'>
        <span className='text-[#2f3c5a] font-semibold'>{bank.accountHolderName}</span>
        <span className='text-[#2f3c5a] text-xs'>
          {bank.bankName} A/c ending in <strong> {bank.accountNo?.slice(-4)}</strong>
        </span>
      </div>
      <div
        className={`ml-auto flex-shrink-0 transition-opacity duration-200 ease-in-out ${isSelected ? 'opacity-100' : 'opacity-0'
          }`}
      >
        <div className='w-6 h-6 flex items-center justify-center'>
          <img src={tickIcon} alt='tick' />
        </div>
      </div>
    </div>
  )
}

const MFPayment = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useRecoilState(userDetailsState)
  const [paymentPayload, setPaymentPayload] = useState(location?.state?.paymentPayload)
  const [orderNumber, setOrderNumber] = useState(location?.state?.orderNumber)
  const clientCode = paymentPayload?.clientCode
  const userData = JSON.parse(localStorage.getItem('userData'))
  const [upiId, setUpiId] = useState(userData?.upiId || '')
  const [isLoading, setIsLoading] = useState(false)
  const { contents: bankDetails } = useRecoilValueLoadable(BankDetails)
  const [showPaymentRequestPopup, setShowPaymentRequestPopup] = useState(false)
  const [selectedOption, setSelectedOption] = useState('UPI')
  const [responseString, setResponseString] = useState('')
  const [modalState, setModalState] = useState({
    isOpen: false,
    status: 'inprogress',
    loading: false,
  })
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const { showError } = useErrorPopup()

  // Approved mandates used to decide whether to show OTM option
  const [approvedMandates, setApprovedMandates] = useState([])

  const [selectedBank, setSelectedBank] = useState(null)

  // Support multiple bank accounts from API response
  const bankAccountsList = (() => {
    const accounts = []

    // If banks array exists, use it (new API format)
    if (Array.isArray(bankDetails?.banks) && bankDetails?.banks.length > 0) {
      bankDetails.banks.forEach((bank) => {
        accounts.push({
          accountNo: bank.accountNo,
          bankName: bank.bankName,
          ifscCode: bank.ifscCode,
          accountHolderName: bank.accountHolderName || bankDetails?.accountHolderName,
        })
      })
    }
    // Fallback to single bank account (backward compatibility)
    else if (bankDetails?.accountNo) {
      accounts.push({
        accountNo: bankDetails.accountNo,
        bankName: bankDetails.bankName,
        ifscCode: bankDetails.ifscCode,
        accountHolderName: bankDetails.accountHolderName,
      })
    }

    return accounts
  })()

  const getSingleBankAccountNo = () =>
    bankAccountsList.length === 1 ? bankAccountsList[0].accountNo : null

  const handlePaymentMode = (option) => {
    setSelectedOption(option)
    setSelectedBank(getSingleBankAccountNo())
  }

  // Auto-select bank when there's only one account (for non-UPI flows)
  useEffect(() => {
    if (bankAccountsList.length === 1 && selectedOption) {
      setSelectedBank(bankAccountsList[0].accountNo)
    }
  }, [selectedOption, bankDetails])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    // Track payment method page viewed
    if (orderNumber && paymentPayload?.totalAmount) {
      PostHogTracker.pageViewed('Payment Method Selection', {
        order_number: orderNumber,
        amount: paymentPayload.totalAmount
      })
    }
  }, [])

  const selectedBankObj = bankAccountsList?.find((b) => b.accountNo === selectedBank) || null
  const activeBankName = (selectedBankObj?.bankName || bankDetails?.bankName || '')?.toUpperCase()
  const activeIfsc = (selectedBankObj?.ifscCode || bankDetails?.ifscCode || '')?.toUpperCase()

  const disableNetBanking =
    activeBankName?.includes('DBS') ||
    activeBankName?.includes('DEVELOPMENT BANK OF SINGAPORE') ||
    activeBankName?.includes('DIGI BANK') ||
    activeIfsc?.startsWith('DBSS')

  const getMandateDetails = async () => {
    try {
      const response = await get('mutualfundservice/api/mutualFund/mandates')

      const approved =
        response?.status === 'SUCCESS' && Array.isArray(response?.data)
          ? response.data.filter((m) => m?.mandateStatus === 'APPROVED')
          : []

      setApprovedMandates(approved)
    } catch (e) {
      setApprovedMandates([])
    }
  }

  useEffect(() => {
    if (!paymentPayload?.totalAmount) return
    getMandateDetails()
  }, [paymentPayload?.totalAmount])

  const shouldShowOTMOption =
    Array.isArray(approvedMandates) &&
    approvedMandates.length > 0 &&
    approvedMandates.some(
      (m) => Number(m?.amount) >= Number(paymentPayload?.totalAmount),
    )

  useEffect(() => {
    if (!shouldShowOTMOption && selectedOption === 'OTM') {
      setSelectedOption('UPI')
    }
  }, [shouldShowOTMOption, selectedOption])

  const paymentStatus = async () => {
    try {
      const response = await post('/mutualfundservice/api/bseStar/mfUpload/paymentStatus', {
        clientCode: paymentPayload?.clientCode,
        orderNo: orderNumber,
      })

      if (response.isError && !showPaymentRequestPopup) {
        return
      }

      if (
        !response.isError &&
        response.status === 'SUCCESS' &&
        response.data !== 'PAYMENT NOT INITIATED FOR GIVEN ORDER'
      ) {
        // Track payment success
        PostHogTracker.paymentSuccess(orderNumber, paymentPayload?.totalAmount)
        PostHogTracker.investmentConfirmationViewed(
          location?.state?.orders?.schemeCode,
          paymentPayload?.totalAmount,
          'One-Time'
        )

        setShowPaymentRequestPopup(false)
        setModalState({
          isOpen: true,
          status: 'success',
          loading: false,
        })
      }

      if (
        !response.isError &&
        response.status === 'SUCCESS' &&
        response.data === 'PAYMENT NOT INITIATED FOR GIVEN ORDER'
      ) {
        // Track payment failure
        PostHogTracker.paymentFailed(
          orderNumber,
          paymentPayload?.totalAmount,
          'Payment not initiated'
        )

        setShowPaymentRequestPopup(false)
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
      }
    } catch (error) {
      console.log('🚀 ~ paymentStatus ~ error:', error)
      PostHogTracker.paymentError(orderNumber, 'polling_error', error.message)
    }
  }

  const oneTimeOrderPayment = async () => {
    try {
      setIsLoading(true)

      // Track payment method selected
      PostHogTracker.paymentMethodSelected(selectedOption, paymentPayload?.totalAmount)

      if (selectedOption === 'UPI' && !upiId) {
        PostHogTracker.formValidationError('Payment', 'upiId', 'UPI ID is required')
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
        setIsLoading(false)
        return
      }

      // user must choose which bank account is being used for payment
      // (UPI id is also linked to a specific bank account)
      if (!selectedBank) {
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
        setIsLoading(false)
        return
      }

      const payload = { ...paymentPayload, orderNumber: orderNumber }

      if (selectedOption === 'UPI') {
        payload.upiId = upiId
        payload.modeOfPayment = 'UPI'
        payload.loopbackURL = ''
        // Track UPI selected
        PostHogTracker.paymentUpiSelected(upiId, paymentPayload?.totalAmount)
      } else if (selectedOption === 'OTM') {
        payload.modeOfPayment = 'OTM'
        payload.loopbackURL = `${env.LOOPBACK_URL}dashboard/mutual-funds/order-payment/status`
      } else {
        payload.modeOfPayment = 'DIRECT'
        payload.loopbackURL = `${env.LOOPBACK_URL}dashboard/mutual-funds/order-payment/status`
        // Track NetBanking selected
        PostHogTracker.paymentNetBankingSelected(bankDetails?.bankName, paymentPayload?.totalAmount)
      }

      payload.accountNo = selectedBank
      payload.bankName = selectedBankObj?.bankName
      payload.ifscCode = selectedBankObj?.ifscCode

      const response = await post('/mutualfundservice/api/bseStar/api/oneTimePayment?newVersion=true', payload)

      setIsLoading(false)

      if (response.isError) {
        PostHogTracker.paymentFailed(
          orderNumber,
          paymentPayload?.totalAmount,
          response?.response?.data?.message || 'Payment API failed'
        )
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
        return
      }

      if (response?.status === 'SUCCESS') {
        // Track payment polling started
        PostHogTracker.paymentPollingStarted(orderNumber)
        if (response?.status === 'SUCCESS' && !response?.message?.includes('One-time payment will be processed using cheque or otm')) {
          setShowPaymentRequestPopup(true)
        }

        if (
          selectedOption !== 'UPI' &&
          payload.modeOfPayment !== 'UPI' &&
          response?.data?.responsestring
        ) {
          setResponseString(response?.data?.responsestring)

          localStorage.setItem(
            'paymentStatusData',
            JSON.stringify({
              response: response?.data?.responsestring,
              clientCode: paymentPayload?.clientCode,
              orderNo: orderNumber,
              totalAmount: paymentPayload?.totalAmount,
            }),
          )
          navigate('/dashboard/mutual-funds/order-payment/status', {
            state: {
              response: response?.data?.responsestring,
              clientCode: paymentPayload?.clientCode,
              orderNo: orderNumber,
              totalAmount: paymentPayload?.totalAmount,
            },
          })
        }



        if (
          response?.status === 'SUCCESS' &&
          selectedOption === 'OTM' &&
          payload.modeOfPayment === 'OTM' &&
          response?.message?.includes('One-time payment will be processed using cheque or otm')
        ) {


          setModalState({
            isOpen: true,
            status: 'inprogress',
            loading: false,
          })
        }

        if (response?.status === 'FAILURE') {
          PostHogTracker.paymentFailed(
            orderNumber,
            paymentPayload?.totalAmount,
            'Payment request failed'
          )
          setModalState({
            isOpen: true,
            status: 'failure',
            loading: false,
          })
        }
      }
    } catch (error) {
      console.log('🚀 ~ oneTimeOrderPayment ~ error:', error)
    }
  }

  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
    // navigate('/dashboard/mutual-funds/orders')
  }

  const handleModalContinue = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
    navigate('/dashboard/mutual-funds/orders')
  }

  const retryOneTimeOrder = async () => {
    try {
      setIsLoading(true)

      const response = await post(
        'mutualfundservice/api/bseStar/mfOrder/order',
        location?.state?.orders,
      )

      setIsLoading(false)

      if (response?.isError) {
        handleModalClose()
        showError('Order Error', response?.response?.error || 'Failed to retry order.', () => { })
      }

      const orderNumberMatch = response?.data?.match(/ORDER NO: (\d+)/)

      const orderNumbers = orderNumberMatch ? orderNumberMatch[1] : null

      if (response.status === 'SUCCESS' && orderNumbers) {
        handleModalClose()

        const newPaymentPayload = {
          clientCode: clientCode?.toUpperCase(),
          orderNumber: orderNumbers,
          totalAmount: paymentPayload?.totalAmount,
        }

        setPaymentPayload(newPaymentPayload)
        setOrderNumber(orderNumbers)
      } else {
        handleModalClose()
        showError('Order Error', response?.data?.responsestring || 'Order retry failed.', () => { })
      }
    } catch (error) {
      console.log('🚀 ~ retryOneTimeOrder ~ error:', error)
    }
  }

  const generateOTP = async () => {
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: userDetails?.phoneNumber,
        email: userDetails?.email,
      },
    )

    if (response.isError) {
      showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => { })
    }

    if (!response.isError) {
      setOpenOTPPopup(true)
    }
  }

  return (
    <>
      {responseString ? null : (
        <>
          {showPaymentRequestPopup ? (
            <MFUpiPayment
              bankDetails={bankDetails}
              setShowPaymentRequestPopup={setShowPaymentRequestPopup}
              paymentStatus={paymentStatus}
              setModalState={setModalState}
            />
          ) : (
            <div className='bg-white'>
              <div className='max-w-[1128px] mx-auto min-h-screen'>
                <div
                  className='flex gap-x-1 items-center font-semibold cursor-pointer lg:py-2.5 p-4 lg:px-0 bg-white'
                  onClick={() => navigate(-1)}
                >
                  <ChevronLeft className='h-5 w-5' />
                  Complete Payment
                </div>

                <div className='flex items-center justify-center min-h-screen w-full bg-white'>
                  <div className='w-[360px] h-[702px]'>
                    <h1 className='font-dm-sans text-base font-medium leading-6 text-center decoration-slice text-[rgba(45,45,45,1)]'>
                      Total payable amount
                    </h1>
                    <p className='font-dm-sans text-[32px] font-semibold leading-[42px] tracking-[-0.04em] text-center decoration-slice text-[rgba(45,45,45,1)] mt-2 mb-12'>
                      ₹ {paymentPayload?.totalAmount}
                    </p>

                    <div>
                      {/* UPI*/}
                      <div
                        onClick={() => {
                          handlePaymentMode('UPI')
                        }}
                        className={`border p-3 cursor-pointer rounded-[16px] mb-6 transition-all  duration-700 ease-out ${selectedOption === 'UPI'
                          ? 'border-2 border-primary-500 shadow-[0px_4px_24px_0px_rgba(65,85,141,0.12)] bg-[rgba(255,255,255,1)]'
                          : 'border-[rgba(190,190,190,1)]'
                          }`}
                      >
                        <div className='flex justify-between items-center'>
                          <div className='flex items-center space-x-2'>
                            <span>
                              <img
                                src={scanner}
                                alt='Doc_Scanner'
                                className={
                                  selectedOption === 'UPI' ? 'filter-none' : 'filter-grayscale'
                                }
                              />
                            </span>
                            <span
                              className={`font-dm-sans text-[20px] font-semibold leading-[28px] tracking-[-0.04em] text-centre decoration-slice ${selectedOption === 'UPI' ? 'text-primary-500' : 'text-black-500'
                                }`}
                            >
                              UPI
                            </span>
                            <span>
                              <img src={spark} alt='Spark' />
                            </span>
                          </div>
                          <span className='font-dm-sans text-sm font-medium leading-5 text-left text-black-300'>
                            Upto ₹ 1 Lakh
                          </span>
                        </div>
                        {selectedOption === 'UPI' && (
                          <div className='mt-6 flex flex-col gap-y-6'>
                            <div>
                              {' '}
                              <label className='font-dm-sans text-[14px] font-medium leading-[20px] text-left decoration-slice text-black-500'>
                                Your UPI address
                              </label>
                              <Input
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value)}
                                type='text'
                                placeholder='address@bank'
                              />
                            </div>

                            <div className='space-y-2'>
                              <p className='font-dm-sans text-[13px] font-medium leading-[18px] text-left text-[#2d2d2d] mb-2'>
                                Select linked bank account to your UPI id
                              </p>
                              {bankAccountsList.map((bank) => (
                                <BankAccountItem
                                  key={bank.accountNo}
                                  bank={bank}
                                  isSelected={selectedBank === bank.accountNo}
                                  onSelect={setSelectedBank}
                                  tickIcon={tick}
                                  bankLogosUrl={env.BANK_LOGOS}
                                />
                              ))}
                            </div>

                            <p className='font-dm-sans text-sm font-medium leading-[20px] text-left decoration-slice text-[#2d2d2d]'>
                              Some banks may offer lesser limit, switch to Net Banking if UPI is
                              unsuccessful
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Net Banking Option */}
                      {!disableNetBanking && (
                        <div
                          onClick={() => {
                            handlePaymentMode('NetBanking')
                          }}
                          className={`border p-3 cursor-pointer rounded-[16px] mb-6 transition-all duration-700 ease-out  ${selectedOption === 'NetBanking'
                            ? 'border-2 border-[#41558d] shadow-[0px_4px_24px_0px_rgba(65,85,141,0.12)] bg-[rgba(255,255,255,1)]'
                            : 'border-[rgba(190,190,190,1)]'
                            }`}
                        >
                          <div className='flex justify-between items-center '>
                            <div className='flex items-center space-x-2 '>
                              <span>
                                <img
                                  src={selectedOption === 'NetBanking' ? additionalImage : account}
                                  alt='account'
                                />
                              </span>
                              <span
                                className={`font-[DM Sans] text-xl font-medium leading-7 tracking-tight text-left ${selectedOption === 'NetBanking'
                                  ? 'text-[#41558d]'
                                  : 'text-[#727272]'
                                  }`}
                              >
                                Net Banking
                              </span>
                            </div>
                            <span className='font-dm-sans text-sm font-medium leading-5 text-left text-[rgba(114,114,114,1)]'>
                              No limit
                            </span>
                          </div>
                          {selectedOption === 'NetBanking' && (
                            <div className='space-y-2 '>
                              {bankAccountsList.map((bank) => (
                                <BankAccountItem
                                  key={bank.accountNo}
                                  bank={bank}
                                  isSelected={selectedBank === bank.accountNo}
                                  onSelect={setSelectedBank}
                                  tickIcon={tick}
                                  bankLogosUrl={env.BANK_LOGOS}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}



                      {shouldShowOTMOption && (
                        <div
                          onClick={() => {
                            handlePaymentMode('OTM')
                          }}
                          className={`border p-3 cursor-pointer rounded-[16px] mb-6 transition-all duration-700 ease-out  ${selectedOption === 'OTM'
                            ? 'border-2 border-[#41558d] shadow-[0px_4px_24px_0px_rgba(65,85,141,0.12)] bg-[rgba(255,255,255,1)]'
                            : 'border-[rgba(190,190,190,1)]'
                            }`}
                        >
                          <div className='flex justify-between items-center '>
                            <div className='flex items-center space-x-2 '>
                              <span>
                                <img
                                  src={selectedOption === 'OTM' ? additionalImage : account}
                                  alt='account'
                                />
                              </span>
                              <span
                                className={`font-[DM Sans] text-xl font-medium leading-7 tracking-tight text-left ${selectedOption === 'OTM'
                                  ? 'text-[#41558d]'
                                  : 'text-[#727272]'
                                  }`}
                              >
                                One Time Mandate
                              </span>
                            </div>
                            <span className='font-dm-sans text-sm font-medium leading-5 text-left text-[rgba(114,114,114,1)]'>
                              No limit
                            </span>
                          </div>
                          {selectedOption === 'OTM' && (
                            <div className='space-y-2 '>
                              {bankAccountsList.map((bank) => (
                                <BankAccountItem
                                  key={bank.accountNo}
                                  bank={bank}
                                  isSelected={selectedBank === bank.accountNo}
                                  onSelect={setSelectedBank}
                                  tickIcon={tick}
                                  bankLogosUrl={env.BANK_LOGOS}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}





                      <Button
                        disabled={!selectedOption || !selectedBank || (selectedOption === 'UPI' && !upiId)}
                        loading={isLoading}
                        className='w-full py-3 px-4 rounded-[12px] bg-[#41558d] disabled:bg-primary-300 font-dm-sans text-[14px] font-medium leading-[20px] text-center decoration-skip-ink-none text-white'
                        onClick={() => selectedOption === 'OTM' ? generateOTP() : oneTimeOrderPayment()}
                      >
                        Proceed
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <PaymentStatusModal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        onContinue={handleModalContinue}
        onRetry={retryOneTimeOrder}
        status={modalState.status}
        loading={isLoading}
        paymentData={{
          amount: paymentPayload?.totalAmount || '0',
          paymentMode: selectedOption === 'UPI' ? 'UPI' : selectedOption === 'OTM' ? 'OTM' : 'Internet Banking',
          date: new Date().toLocaleString(),
        }}
      />


      {openOTPPopup && (
        <OrderOtpVerification
          phoneNumber={userDetails?.phoneNumber}
          openOTPPopup={openOTPPopup}
          setOpenOTPPopup={setOpenOTPPopup}
          email={userDetails?.email}
          handleSipOrOT={oneTimeOrderPayment}
        />
      )}
    </>
  )
}

export default MFPayment

